import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EmptyState, ErrorState, ListaSelecao, Modal, Skeleton } from '../../../ui';

import { RootState } from '../../../store/store';
import { setCodigoCooperativaSelecionada } from '../../../store/cotacaoSlice';
import { CooperativaVinculada, buscarUsuarioPorId } from '../../../api/user/userApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Escolha da cooperativa do consultor (reescrito na at-59 sobre o `Modal` do kit).
 * É o **par** do `ModalRegionaisUsuario`, migrado na at-58 — mesmas decisões.
 *
 * ⚠️ **É componente de DOMÍNIO**, e por isso mora em `components/`: lê o `userId`
 * do Redux, chama a API de usuário e grava um efeito colateral no fluxo de
 * cotação. O que ele reaproveita do kit é puro.
 *
 * ## O que a reescrita mudou
 *
 *   1. **Visual do design system** e **lista navegável pelo teclado** — eram
 *      `<li onClick>`: `li` não recebe foco nem responde a Enter, então escolher a
 *      cooperativa era **impossível sem mouse**.
 *   2. **Falha deixou de ser muda**: o `catch` mostrava toast e deixava a lista
 *      vazia, indistinguível de "você não tem cooperativa vinculada" — que é um
 *      problema com outra solução. Agora `ErrorState` com "Tentar novamente".
 *   3. **O vazio explica o que fazer**: sem cooperativa o consultor **não
 *      consegue seguir** com a adesão, e o vínculo é feito pelo TI em
 *      `/ti/usuarios`.
 *   4. **Esc, clique fora e foco** vêm do `Modal`.
 *
 * ## O que NÃO mudou, de propósito
 *
 * ⚠️ **As props são as mesmas** (em inglês). 📌 **Atualização da at-62:**
 * `views/cotacao` foi migrada e não há mais consumidor legado — traduzi-las é da
 * frente D.
 * ⚠️ **O `setCodigoCooperativaSelecionada` continua sendo despachado**, e a at-62
 * **não** o removeu de propósito: é assim que `codigoCooperativaSelecionada`
 * chega ao slice, e `/cotacao-produtos` lê aquele campo para buscar os produtos
 * do perfil.
 * ⚠️ **`import '../regionais/ModalRegionais.css'` foi removido** — nenhuma classe
 * de lá é mais usada aqui. É seguro porque **`ModalRegionais.tsx` ainda importa**
 * aquele arquivo (e é usado por 2 telas de TI: `/ti/criar-usuario` e
 * `/ti/usuarios`), então os globais acidentais que ele carrega — `.carregando`
 * (36 telas), `.mt-20` (10) e `.modal-busca-input` — continuam no bundle.
 * 🔴 **O aviso no topo daquele CSS dizia que este componente era o último
 * importador: era falso**, e foi corrigido na at-59.
 *
 * 📌 **at-67:** a lista acima citava também o `ModalCriarUsuario`, que era **código
 * morto** (zero importadores em todo o `src`) e foi apagado junto com o
 * `ModalTrocaDeSenha`. Os dois nem consumiam este CSS — importavam
 * `modalTrocaSenha.css`.
 */

/*
 * ⚠️ O tipo vem do `userApi` desde a at-68 — ver a nota igual no `ModalRegionaisUsuario`:
 * a cópia local dizia `cidade`/`estado` obrigatórios, e as duas colunas são nuláveis no
 * espelho do SGA.
 */
type Cooperativa = CooperativaVinculada;

interface ModalCooperativasUsuarioProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCooperativa: (cooperativa: Cooperativa) => void;
}

type Estado = 'carregando' | 'ok' | 'erro';

const ModalCooperativasUsuario: React.FC<ModalCooperativasUsuarioProps> = ({
  isOpen,
  onClose,
  onSelectCooperativa,
}) => {
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.auth.userId);

  const [cooperativas, setCooperativas] = useState<Cooperativa[]>([]);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');

  const buscar = useCallback(async () => {
    if (!userId) return;

    setEstado('carregando');
    try {
      const usuario = await buscarUsuarioPorId(userId);

      // Resposta sem o array é resposta inesperada, não lista vazia — tratar as
      // duas como "nenhuma cooperativa" esconderia um problema de contrato.
      if (!usuario || !Array.isArray(usuario.cooperativas)) {
        setErro('A resposta do servidor não trouxe as cooperativas do seu usuário.');
        setEstado('erro');
        return;
      }

      setCooperativas(usuario.cooperativas);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar as suas cooperativas.'));
      setEstado('erro');
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) void buscar();
  }, [isOpen, buscar]);

  const escolher = (cooperativa: Cooperativa) => {
    // Efeito colateral legado do fluxo de cotação — ver o aviso no topo.
    dispatch(setCodigoCooperativaSelecionada(cooperativa.codigo_cooperativa));
    onSelectCooperativa(cooperativa);
    onClose();
  };

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Cooperativa"
      descricao="Define cota, produtos e rateio do veículo nas próximas etapas."
      tamanho="sm"
      aoFechar={onClose}
    >
      {estado === 'carregando' && <Skeleton linhas={3} altura="tw-h-12" />}

      {estado === 'erro' && <ErrorState mensagem={erro} aoTentarNovamente={() => void buscar()} />}

      {estado === 'ok' && (
        <ListaSelecao
          itens={cooperativas}
          chave={(c) => c.id ?? c.codigo_cooperativa}
          rotulo={(c) => c.nome}
          /* Cidade/UF distinguem cooperativas de nome parecido — a resposta já
             trazia os campos e a lista antiga os descartava. */
          detalhe={(c) => [c.cidade, c.estado].filter(Boolean).join(' / ') || undefined}
          aoEscolher={escolher}
          rotuloAcessivel="Cooperativas vinculadas ao seu usuário"
          vazio={
            <EmptyState
              icone="local"
              titulo="Nenhuma cooperativa vinculada ao seu usuário"
              descricao="Sem uma cooperativa você não consegue concluir a adesão. Peça ao TI para vincular a sua cooperativa em Administração › Usuários."
            />
          }
        />
      )}
    </Modal>
  );
};

export default ModalCooperativasUsuario;
